import React, { useEffect, useState } from 'react';
import { formatEmailAmount } from '../utils/formatNumberInput';
import { customersService } from '../lib/db/customers';
import { quotesService } from '../lib/db/quotes';
import { contractsService } from '../lib/db/contracts';
import { projectsService } from '../lib/db/projects';
import {
  UsersIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

interface QuoteSummary {
  id: string;
  quote_number: string;
  quote_name: string;
  customer_name: string;
  project_name: string;
  total_amount: number;
}

interface DashboardStats {
  weeklyStats: {
    newCustomers: number;
    newContracts: number;
  };
  newQuotes: QuoteSummary[];
  signedQuotes: QuoteSummary[];
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    weeklyStats: {
      newCustomers: 0,
      newContracts: 0,
    },
    newQuotes: [],
    signedQuotes: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const today = new Date();
      const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneWeekAgoStr = oneWeekAgo.toISOString().split('T')[0];

      // 獲取過去一週新增的客戶
      const allCustomers = await customersService.getAll();
      const newCustomers = allCustomers.filter(c => c.created_at >= oneWeekAgoStr).length;

      // 獲取過去一週新增的合約
      const allContracts = await contractsService.getAll();
      const newContracts = allContracts.filter(c => c.created_at >= oneWeekAgoStr).length;

      // 獲取過去一週新增與簽回的報價單明細（客戶名稱從 customers 表取得，避免報價單內 denormalized 資料過期）
      const allQuotes = await quotesService.getAll();
      const customerMap = new Map(allCustomers.map(c => [c.id, c.company]));
      const quoteProjectIds = [...new Set(allQuotes.map(q => q.project_id).filter(Boolean))] as string[];
      const projectMap = new Map<string, string>();
      if (quoteProjectIds.length > 0) {
        const allProjects = await projectsService.getAll();
        allProjects.forEach(p => projectMap.set(p.id, p.name));
      }
      const parseAmount = (v: unknown): number => {
        if (typeof v === 'number' && !isNaN(v)) return v;
        if (typeof v === 'string') return parseFloat(v) || 0;
        return 0;
      };
      const toQuoteSummary = (q: typeof allQuotes[0]) => ({
        id: q.id,
        quote_number: q.quote_number,
        quote_name: q.quote_name || '-',
        customer_name: (q.customer_id && customerMap.get(q.customer_id)) || q.customer_name || '-',
        project_name: (q.project_id && projectMap.get(q.project_id)) || '-',
        total_amount: parseAmount(q.discounted_total ?? q.total_amount ?? 0),
      });
      const newQuotesList = allQuotes
        .filter(q => q.created_at >= oneWeekAgoStr)
        .map(toQuoteSummary);
      const signedQuotesList = allQuotes
        .filter(q => {
          if (q.status !== 'signed_back') return false;
          const signDate = q.signed_back_date || q.updated_at;
          return signDate >= oneWeekAgoStr;
        })
        .map(toQuoteSummary);

      setStats({
        weeklyStats: {
          newCustomers: newCustomers || 0,
          newContracts: newContracts || 0,
        },
        newQuotes: newQuotesList,
        signedQuotes: signedQuotesList,
      });
    } catch {
      // Error fetching dashboard data
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ci-text">儀表板</h1>
        <p className="mt-1 text-sm text-gray-500">
          系統內近期異動
        </p>
      </div>

      {/* 過去這一週的重要資訊 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-ci-text mb-4">過去這一週的重要資訊</h2>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8 text-primary" />
            </div>
            <div className="ml-3">
              <div className="flex items-center">
                <span className="text-sm font-medium text-ci-text">客戶</span>
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ci-secondary/30 text-primary">
                  {stats.weeklyStats.newCustomers}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {stats.weeklyStats.newCustomers > 0 ? `過去一週新增 ${stats.weeklyStats.newCustomers} 位客戶` : '過去一週無新增客戶'}
              </p>
            </div>
          </div>

          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8 text-success" />
            </div>
            <div className="ml-3">
              <div className="flex items-center">
                <span className="text-sm font-medium text-ci-text">合約</span>
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/20 text-success">
                  {stats.weeklyStats.newContracts}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {stats.weeklyStats.newContracts > 0 ? `過去一週新增 ${stats.weeklyStats.newContracts} 份合約` : '過去一週無新增合約'}
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* 報價單區塊 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-ci-text mb-4">過去這一週的報價單</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              新增報價單 ({stats.newQuotes.length}) · 總額 NT$ {formatEmailAmount(stats.newQuotes.reduce((sum, q) => sum + q.total_amount, 0))}
            </h3>
            <div className="space-y-2">
              {stats.newQuotes.length > 0 ? (
                stats.newQuotes.map((q) => (
                  <div key={q.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-ci-text">{q.quote_number} - {q.quote_name}</p>
                      <p className="text-xs text-gray-600">客戶: {q.customer_name} · 專案: {q.project_name}</p>
                    </div>
                    <p className="font-medium text-ci-text">NT$ {formatEmailAmount(q.total_amount)}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 py-4">過去一週無新增報價單</p>
              )}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              簽回報價單 ({stats.signedQuotes.length}) · 總額 NT$ {formatEmailAmount(stats.signedQuotes.reduce((sum, q) => sum + q.total_amount, 0))}
            </h3>
            <div className="space-y-2">
              {stats.signedQuotes.length > 0 ? (
                stats.signedQuotes.map((q) => (
                  <div key={q.id} className="flex items-center justify-between p-3 bg-success/10 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-ci-text">{q.quote_number} - {q.quote_name}</p>
                      <p className="text-xs text-gray-600">客戶: {q.customer_name} · 專案: {q.project_name}</p>
                    </div>
                    <p className="font-medium text-ci-text">NT$ {formatEmailAmount(q.total_amount)}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 py-4">過去一週無簽回報價單</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 